/**
 * usePresentationKeyboard
 *
 * Global keyboard-event hook used by `PresentationMode` to drive the
 * slide-show (PowerPoint F5-equivalent).  Installs a single
 * `window.addEventListener("keydown", ...)` when `enabled` is true and
 * routes keys to the caller-supplied callbacks.
 *
 * Keys handled:
 *   - Next:  ArrowRight, Space (" "), PageDown, "n", "N", Enter
 *   - Prev:  ArrowLeft, PageUp, "p", "P", Backspace (only when buffer empty)
 *   - Home:  Home
 *   - End:   End
 *   - Exit:  Escape, "f", "F"
 *   - Blank: "b", "B"
 *   - Thumbs: "s", "S" (toggles the thumbnail strip — optional callback)
 *   - Laser:  "l", "L" (toggles the laser-pointer overlay — optional)
 *   - Pen:    "d", "D" (toggles the pen/annotation overlay — optional)
 *   - Clear:  "e", "E" (clears strokes on the current slide — optional)
 *   - Jump:  digits 0-9 accumulate into an internal buffer; pressing
 *     Enter while digits are buffered jumps to that slide (1-indexed)
 *     via `onJump(n)` instead of advancing.  Backspace pops the last
 *     digit while the buffer is non-empty (PowerPoint-parity edit
 *     behavior); only once the buffer is empty does Backspace fall
 *     through to `onPrev`.  Any non-digit / non-Backspace key (or a 3s
 *     idle) clears the buffer.
 *
 * Esc priority is handled by the *caller*: the host component wraps the
 * `onExit` callback so that when the presenter is in pen/laser mode,
 * the first Esc returns to nav-mode and only a second Esc exits
 * presentation.  Keeping that logic outside the hook keeps the keyboard
 * routing a pure switch-statement.
 */
import { useEffect, useRef } from "react"

export interface UsePresentationKeyboardOptions {
  onNext: () => void
  onPrev: () => void
  onHome: () => void
  onEnd: () => void
  onExit: () => void
  onBlank: () => void
  onJump: (slideNumber: number) => void
  /**
   * Optional — toggles the thumbnail strip (S/s key).  Optional so
   * existing consumers that don't render a thumbnail strip continue
   * to work without passing a no-op.
   */
  onToggleThumbs?: () => void
  /**
   * Optional — toggles the laser-pointer overlay (L/l key).
   */
  onToggleLaser?: () => void
  /**
   * Optional — toggles the pen/annotation overlay (D/d key).
   */
  onTogglePen?: () => void
  /**
   * Optional — clears all strokes on the current slide (E/e key).
   */
  onClearStrokes?: () => void
  /**
   * Optional — undoes the last stroke on the current slide (U/u key
   * or Ctrl/Cmd+Z).  No-op outside pen mode is enforced by the host.
   */
  onUndoStroke?: () => void
  enabled: boolean
}

const JUMP_BUFFER_TIMEOUT_MS = 3000

export function usePresentationKeyboard({
  onNext,
  onPrev,
  onHome,
  onEnd,
  onExit,
  onBlank,
  onJump,
  onToggleThumbs,
  onToggleLaser,
  onTogglePen,
  onClearStrokes,
  onUndoStroke,
  enabled,
}: UsePresentationKeyboardOptions): void {
  // Callbacks kept in refs so the listener (attached once per enabled-toggle)
  // always sees the latest handlers without being torn down on every render.
  const handlersRef = useRef({
    onBlank,
    onClearStrokes,
    onEnd,
    onExit,
    onHome,
    onJump,
    onNext,
    onPrev,
    onToggleLaser,
    onTogglePen,
    onToggleThumbs,
    onUndoStroke,
  })
  handlersRef.current = {
    onBlank,
    onClearStrokes,
    onEnd,
    onExit,
    onHome,
    onJump,
    onNext,
    onPrev,
    onToggleLaser,
    onTogglePen,
    onToggleThumbs,
    onUndoStroke,
  }

  useEffect(() => {
    if (!enabled) return

    let jumpBuffer = ""
    let jumpTimer: ReturnType<typeof setTimeout> | null = null

    const clearJump = () => {
      jumpBuffer = ""
      if (jumpTimer !== null) {
        clearTimeout(jumpTimer)
        jumpTimer = null
      }
    }

    const resetJumpTimer = () => {
      if (jumpTimer !== null) clearTimeout(jumpTimer)
      jumpTimer = setTimeout(() => {
        jumpBuffer = ""
        jumpTimer = null
      }, JUMP_BUFFER_TIMEOUT_MS)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const { key } = event
      const h = handlersRef.current

      // Ctrl/Cmd + Z — undo last stroke on the current slide.  Caller
      // gates this to pen-mode; we just route the chord here.  Checked
      // before the digit / Enter handling so the modifier never
      // interferes with the jump buffer.
      if ((event.ctrlKey || event.metaKey) && (key === "z" || key === "Z")) {
        if (h.onUndoStroke) {
          event.preventDefault()
          h.onUndoStroke()
        }
        return
      }

      // Digits build up a jump buffer — do NOT consume as navigation.
      if (key >= "0" && key <= "9") {
        event.preventDefault()
        jumpBuffer += key
        resetJumpTimer()
        return
      }

      // Enter: jump if buffered digits, else advance.
      if (key === "Enter") {
        event.preventDefault()
        if (jumpBuffer.length > 0) {
          const n = parseInt(jumpBuffer, 10)
          clearJump()
          if (!Number.isNaN(n)) h.onJump(n)
        } else {
          h.onNext()
        }
        return
      }

      // Backspace while editing the jump buffer: pop the last digit
      // (mirrors the usual text-input edit model) and keep the 3s idle
      // timer alive.  Only when the buffer is empty does Backspace fall
      // through to prev-slide navigation.
      if (key === "Backspace" && jumpBuffer.length > 0) {
        event.preventDefault()
        jumpBuffer = jumpBuffer.slice(0, -1)
        if (jumpBuffer.length === 0) {
          // Clear the idle timer too — no pending jump to flush.
          if (jumpTimer !== null) {
            clearTimeout(jumpTimer)
            jumpTimer = null
          }
        } else {
          resetJumpTimer()
        }
        return
      }

      // Any other key invalidates the jump buffer before being routed.
      if (jumpBuffer.length > 0) clearJump()

      switch (key) {
        case "ArrowRight":
        case " ":
        case "PageDown":
        case "n":
        case "N":
          event.preventDefault()
          h.onNext()
          return
        case "ArrowLeft":
        case "PageUp":
        case "p":
        case "P":
        case "Backspace":
          event.preventDefault()
          h.onPrev()
          return
        case "Home":
          event.preventDefault()
          h.onHome()
          return
        case "End":
          event.preventDefault()
          h.onEnd()
          return
        case "Escape":
        case "f":
        case "F":
          event.preventDefault()
          h.onExit()
          return
        case "b":
        case "B":
          event.preventDefault()
          h.onBlank()
          return
        case "s":
        case "S":
          // Thumbnail-strip toggle is optional — skip gracefully when
          // the host component didn't wire a handler.
          if (h.onToggleThumbs) {
            event.preventDefault()
            h.onToggleThumbs()
          }
          return
        case "l":
        case "L":
          if (h.onToggleLaser) {
            event.preventDefault()
            h.onToggleLaser()
          }
          return
        case "d":
        case "D":
          if (h.onTogglePen) {
            event.preventDefault()
            h.onTogglePen()
          }
          return
        case "e":
        case "E":
          if (h.onClearStrokes) {
            event.preventDefault()
            h.onClearStrokes()
          }
          return
        case "u":
        case "U":
          if (h.onUndoStroke) {
            event.preventDefault()
            h.onUndoStroke()
          }
          return
        default:
          return
      }
    }

    // Capture-phase listener on `window` so the presentation shortcuts
    // (including D/d to toggle pen mode) always fire, even if some
    // descendant element — e.g. the annotation canvas after pointer
    // capture — would otherwise consume the event via
    // `event.stopPropagation()` in its own listeners. Without `capture`
    // a stray stopPropagation on a child would leave the presenter
    // stuck in pen/laser mode with no keyboard exit.
    window.addEventListener("keydown", handleKeyDown, true)
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true)
      if (jumpTimer !== null) clearTimeout(jumpTimer)
    }
  }, [enabled])
}
